/**
 * VIN (Vehicle Identification Number) validation utilities.
 *
 * Format validation only (no check digit). European VINs do not require a
 * check digit; North American ones do, but this product targets Estonia.
 *
 * ISO 3779 (17 characters; A-H, J-N, P-R, Z and digits 0-9; no I, O, Q) only
 * applies to vehicles built after ~1981. Older vehicles — a substantial
 * share of Estonia's fleet is Soviet-built (Lada/VAZ, GAZ, Moskvich, UAZ,
 * ZAZ, ...) — predate the standard and carry a shorter chassis/frame number
 * instead, of no fixed length, sometimes with I, O or Q.
 *
 * So a session `vinCode` (what the caller says is on the vehicle) is any 1-17
 * Latin letters or digits (`isValidVinCode`). The photo check matches a code
 * of 4+ characters against the OCR text (`vinsEqual` / `textContainsVin`)
 * without any pattern; `runAiChecks` skips it for 1-3 characters (NSF-41). The stricter `VIN_PATTERN` only picks a candidate out of OCR text
 * when the session code is not on the photo, so noise is not reported as a VIN.
 */

/** OCR candidate: 4-17 characters, same restricted charset as ISO 3779 (no I, O, Q), after uppercasing. */
export const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{4,17}$/;

/** Session `vinCode`: 1-17 Latin letters or digits, after uppercasing. No diacritics, spaces or symbols. */
export const VIN_CODE_PATTERN = /^[A-Z0-9]{1,17}$/;

export function normalizeVin(vin: string): string {
  return vin.trim().toUpperCase();
}

/**
 * @param vin - VIN to validate (case-insensitive)
 * @returns true if the VIN matches the accepted format
 */
export function isValidVin(vin: string | null | undefined): boolean {
  if (!vin) {
    return false;
  }
  // Do not trim: surrounding whitespace is invalid (photo OCR and create both
  // normalize before calling this when they intend to accept padded input).
  return VIN_PATTERN.test(vin.toUpperCase());
}

/** @param vin - Session `vinCode` to validate (case-insensitive, not trimmed) */
export function isValidVinCode(vin: string): boolean {
  return VIN_CODE_PATTERN.test(vin.toUpperCase());
}

/** Case-insensitive equality after trim + uppercase. */
export function vinsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) {
    return false;
  }
  return normalizeVin(a) === normalizeVin(b);
}

/** OCR reads 0 as O and 1 as I. A VIN never contains I, O or Q (ISO 3779), so fold them back. */
function foldOcrConfusions(text: string): string {
  return text.toUpperCase().replace(/[IOQ]/g, (c) => (c === 'I' ? '1' : '0'));
}

/**
 * True when `text` contains `expectedVin`, ignoring case, spaces, and other
 * non-alphanumerics (OCR often inserts gaps on a VIN plate, and splits a long
 * VIN across lines). I/O/Q in the text are read as 1/0/0.
 *
 * A full 17-character VIN may sit anywhere in the text. A shorter chassis
 * number must be whole consecutive tokens on one line, so digits from
 * neighbouring codes (e.g. "4450" inside "1445050", or "12" and "34" on
 * different lines) cannot fake a match.
 */
export function textContainsVin(text: string, expectedVin: string): boolean {
  const expected = foldOcrConfusions(expectedVin).replace(/[^A-Z0-9]/g, '');
  if (!expected) {
    return false;
  }
  const folded = foldOcrConfusions(text);
  if (expected.length === 17) {
    return folded.replace(/[^A-Z0-9]/g, '').includes(expected);
  }
  return folded.split('\n').some((line) => {
    const tokens = line.split(/[^A-Z0-9]+/).filter(Boolean);
    return tokens.some((_, start) => {
      let run = '';
      for (let i = start; i < tokens.length && run.length < expected.length; i++) {
        run += tokens[i];
      }
      return run === expected;
    });
  });
}
